import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { registerUser } from '../../services/api';
import {
  CategoryResponseModel,
  RegisterInputModel,
  SearchTermInputModel,
} from '../../types/models';

interface FormValues {
  firstName: string;
  lastName: string;
  fullName: string;
  displayName: string;
  headline: string;
  dateOfBirth?: string;
  isProfessional: boolean;
  searchTerms: SearchTermInputModel[];
  categories: CategoryResponseModel[];
}

interface OnboardingContextType {
  form: UseFormReturnType<FormValues>;
  updateIsProfessional: (value: boolean) => void;
  isProfessional: boolean;
  onSubmit: () => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  resetForm: () => void;
}

interface OnboardingProviderProps {
  children: ReactNode;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined
);

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const form = useForm<FormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      fullName: '',
      displayName: '',
      headline: '',
      dateOfBirth: undefined,
      isProfessional: false,
      searchTerms: [],
      categories: [],
    },
    validate: {
      firstName: (value) => {
        if (!value || value.trim() === '') return 'First name is required';
        if (value.length > 20)
          return 'The first name can only be a maximum of 20 characters';
        if (!/^[a-zA-Z\s-]{1,20}$/.test(value))
          return 'The first name cannot contain special characters';
        return null;
      },
      lastName: (value) => {
        if (!value || value.trim() === '') return 'Surname is required';
        if (value.length > 20)
          return 'The surname can only be a maximum of 20 characters';
        if (!/^[a-zA-Z\s-]{1,20}$/.test(value))
          return 'The surname cannot contain special characters';
        return null;
      },
      fullName: (value) => {
        if (!value || value.trim() === '') return 'Full name is required';
        if (value.length > 41)
          return 'The full name can only be a maximum of 41 characters';
        if (!/^[a-zA-Z\s-]{1,41}$/.test(value))
          return 'The full name cannot contain special characters';
        return null;
      },
      displayName: (value, values) => {
        if (values.isProfessional) {
          if (!value || value.trim() === '') return 'Display name is required';
          if (value.length > 100)
            return 'The display name can only be a maximum of 100 characters';
          if (!/^[a-zA-Z0-9\s_-]{1,100}$/.test(value))
            return 'The display name can only contain alphanumeric characters, spaces, underscores, and hyphens';
        }
        return null;
      },
      headline: (value, values) => {
        if (values.isProfessional && (!value || value.trim() === '')) {
          return 'Headline is required for professional accounts';
        }
        if (value && value.length > 50)
          return 'The headline can only be a maximum of 50 characters';
        return null;
      },
      dateOfBirth: (value) => {
        if (!value) return 'Date of birth is required';

        const birthDate = new Date(value);
        const today = new Date();

        if (isNaN(birthDate.getTime())) return 'Please enter a valid date';

        if (birthDate > today) return 'Date of birth cannot be in the future';

        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        if (age < 16)
          return 'You must be at least 16 years old to create an account';

        return null;
      },
    },
    onValuesChange: () => {},
  });

  const getPayload = (): RegisterInputModel => {
    return {
      firstName: form.values.firstName,
      lastName: form.values.lastName,
      fullName: form.values.fullName,
      displayName: form.values.displayName,
      headline: form.values.headline,
      dateOfBirth: form.values.dateOfBirth ?? '',
      isProfessional: form.values.isProfessional,
      searchTerms: form.values.searchTerms,
      categories: form.values.categories,
      enabled: true,
    };
  };

  const {
    mutate: submitForm,
    isPending: isLoading,
    isError,
    isSuccess,
    reset,
  } = useMutation({
    mutationFn: () =>
      registerUser(getPayload(), localStorage.getItem('token') || ''),
  });

  const resetForm = () => {
    reset();
  };

  const onSubmit = () => {
    form.validate();
    if (form.isValid()) {
      submitForm();
    }
  };

  useEffect(() => {
    const fullName = `${form.values.firstName} ${form.values.lastName}`.trim();
    form.setFieldValue('fullName', fullName);
  }, [form.values.firstName, form.values.lastName]);

  const updateIsProfessional = (value: boolean) => {
    form.setFieldValue('isProfessional', value);
  };

  return (
    <OnboardingContext.Provider
      value={{
        form,
        updateIsProfessional,
        isProfessional: form.values.isProfessional,
        onSubmit,
        isLoading,
        isError,
        isSuccess,
        resetForm,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);

  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }

  return context;
};
